import { useEffect, useRef, useState } from "react";
import { ClubService } from "../../../../services/clubService";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/Toast";
import { BULK_CHUNK, chunk, msg } from "./manageUtils";

/** Applications tab data + actions (AdminScreen :763, :2525 handleApplicationAction, :2634 handleBulkApplicationAction). */
export default function useApplications(club) {
  const { user } = useAuth();
  const toast = useToast();
  const clubId = club.id;
  const [state, setState] = useState({ loading: true, error: null });
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const appsRef = useRef(apps);
  const clubRef = useRef(clubId);
  appsRef.current = apps;
  clubRef.current = clubId;

  useEffect(() => {
    let live = true;
    setState({ loading: true, error: null });
    setSelected(new Set());
    ClubService.getClubApplications(clubId).then(({ data, error }) => {
      if (!live) return;
      if (error) { setState({ loading: false, error }); return; }
      setApps(data || []);
      setState({ loading: false, error: null });
    });
    return () => { live = false; };
  }, [clubId, reload]);

  const allSelected = apps.length > 0 && apps.every((a) => selected.has(a.id));
  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(apps.map((a) => a.id)));

  // Web has no shared member cache (each tab refetches on open), so the mobile "synthetic member row"
  // patch after an approval is unnecessary: Overview and Members re-read the real roster.
  const restore = (snapshot, failedIds) => {
    if (clubRef.current !== clubId || failedIds.size === 0) return;
    setApps((prev) => {
      const have = new Set(prev.map((a) => a.id));
      const missing = snapshot.filter((a) => failedIds.has(a.id) && !have.has(a.id));
      if (!missing.length) return prev;
      const combined = new Set([...have, ...missing.map((a) => a.id)]);
      const ordered = snapshot.filter((a) => combined.has(a.id));
      const extra = prev.filter((a) => !snapshot.some((s) => s.id === a.id));
      return [...ordered, ...extra];
    });
  };

  const act = async (id, action) => {
    if (!user?.uid) { toast("You must be logged in to review applications", "alert"); return; }
    const snapshot = appsRef.current;
    const item = snapshot.find((a) => a.id === id);
    if (!item) return;
    setApps((prev) => prev.filter((a) => a.id !== id));
    setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    const { error } = await ClubService.reviewApplication(id, action, user.uid);
    if (error) {
      restore(snapshot, new Set([id]));
      toast(msg(error, "Failed to process application"), "alert");
    }
  };

  const bulk = async (action) => {
    if (selected.size === 0 || bulkBusy) return;
    if (!user?.uid) { toast("You must be logged in to review applications", "alert"); return; }
    setBulkBusy(true);
    const ids = [...selected];
    const idsSet = new Set(ids);
    const snapshot = appsRef.current;
    setApps((prev) => prev.filter((a) => !idsSet.has(a.id)));
    try {
      // Web-side guard: the server caps one bulk approval at 100 applications (BULK_APPROVE_MAX).
      const results = [];
      for (const part of chunk(ids, BULK_CHUNK)) {
        const out = await ClubService.reviewApplicationsBulk(part, action, user.uid);
        part.forEach((_, i) => results.push(out?.[i]));
      }
      const failed = new Set(ids.filter((_, i) => results[i]?.error || results[i]?.data?.success !== true));
      restore(snapshot, failed);
      if (failed.size > 0) {
        toast(`Partial success: ${ids.length - failed.size} of ${ids.length} applications ${action === "approve" ? "approved" : "rejected"}. ${failed.size} failed.`, "alert");
      }
      setSelected(new Set([...failed]));
    } catch (e) {
      restore(snapshot, idsSet);
      toast(msg(e, "Failed to process applications"), "alert");
    } finally {
      setBulkBusy(false);
    }
  };
  return { state, apps, selected, bulkBusy, allSelected, toggle, toggleAll, act, bulk, retry: () => setReload((n) => n + 1) };
}
